







import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { RenderPostModel } from "@/services/render/types";
import {
    HtmlPostProperties,
    HtmlCSS
} from "@/services/posts/postTypes/html";
import { Style } from "@/services/styles/types";
import Macro from "./Macro.vue";
import { RuntimeTemplateCompiler } from "vue-runtime-template-compiler";
import { setNextItem } from "../types";

@Component({
    components: {
        RuntimeTemplateCompiler,
        Macro
    }
})
export default class HtmlRender extends Vue {
    @Prop() private item!: RenderPostModel;
    @Prop() private itemStyle!: Style; // it's used only in style form preview
    @Prop() private active!: number;
    @Prop() private containerHeight!: number;
    @Prop() private containerWidth!: number;
    @Prop() private numberOfPosts!: number;

    // local state
    addedVideoEvents = false;
    allowRender = false;
    update = false;
    html = this.propertiesModel.html;
    firstRender = true;

    @Watch("item", { immediate: true, deep: false })
    onItemChanged(value: RenderPostModel) {
        if (value && value.properties) {
            const properties = value.properties as HtmlPostProperties;
            this.renderHtml(properties);
        }
    }

    @Watch("propertiesModel") t() {
        // Since the runtime compiler doesn't automatically update based on changes to the html, we need to manually update
        if (this.html !== this.propertiesModel.html) {
            this.html = this.propertiesModel.html;
            this.update = !this.update;
        }
    }

    renderHtml(properties: HtmlPostProperties) {
        if (properties.html && properties.html.length > 0) {
            properties.html = this.placeMacros();
            if (this.html !== this.propertiesModel.html || this.firstRender) {
                (this.item.properties as HtmlPostProperties).html = this.processHtmlContent(properties.html);
            }
            if (this.firstRender) this.firstRender = false;
            this.allowRender = true;
            if (this.active) {
                this.playVideoIfPresent();
            }
        }
    }

    placeMacros() {
        const macros = ["CDOWNDAYS", "CLOCALDAYS", "CGLOBALDAYS", "CDOWNHOURS", "CDOWNMINUTES", "CDOWNDHM", "CUPDHM"];
        let htmlString: any = this.propertiesModel.html;
        for (let i = 0; i < macros.length; i++) {
            const macro = macros[i];

            // For every item in the macro array, we check if we have a match to that macro in the html string.
            // const reg = new RegExp(`(?<=\\[${macro}:\\s*).*?(?=\\s*\\])`, "gs");
            const reg = new RegExp(`\\[${macro}:.+?\\]`, "gi");
            const matches = htmlString.match(reg);
            if (matches !== null) {
                for (let j = 0; j < matches.length; j++) {
                    let element = matches[j];
                    element = element.toUpperCase().replace(`[${macro}:`, "");
                    element = element.replace("]", "");
                    const macroReg = new RegExp(`\\[${macro}:${element}\\]`, "i");

                    // If we do, and it's either of these two types, we do some special handling.
                    // We replace Days, Hours and Minutes with individual macros keeping track of the same time. They have the "displayType" property to differentiate them.
                    if (macro === "CDOWNDHM" || macro === "CUPDHM") {
                        const types = ["DAYS", "HOURS", "MINUTES"];
                        for (let k = 0; k < types.length; k++) {
                            const type = types[k];
                            if (htmlString.toUpperCase().includes(`[${type}]`)) {
                                const regType = new RegExp(`\\[${type}\\]`, "i");
                                htmlString = htmlString.replace(regType, `<macro displayType="${type}" type="${macro}" element="${element}"></macro>`);
                            }
                        }
                        htmlString = htmlString.replace(macroReg, ""); // After we have replaced the Days, Hours and Minutes we can remove the original string holding the time.
                        continue;
                    }

                    // If it's neither of the two above, this is the default for all macros. Replace their text with a macro component.
                    htmlString = htmlString.replace(macroReg, `<macro type="${macro}" element="${element}"></macro>`);
                }
            }
        }

        htmlString = htmlString.replace(/\[WEEKNUMBER\]/i, `<macro type="WEEKNUMBER"></macro>`);
        return htmlString;
    }

    // transform into start method
    setNextItemTimer = 0;
    @Watch("active", { immediate: true, deep: true })
    onActiveChange(value: number) {
        if (value !== 0) {
            this.playVideoIfPresent();
            clearTimeout(this.setNextItemTimer);
            this.setNextItemTimer = setTimeout(
                this.setNextItem,
                this.propertiesModel.showTime * 1000);
        }
    }

    get propertiesModel(): HtmlPostProperties {
        return this.item.properties
            ? (this.item.properties as HtmlPostProperties)
            : ({} as HtmlPostProperties);
    }

    get styleModel(): string {
        const css = this.item.css && Object.entries(this.item.css).length > 0 && this.item.css.constructor === Object ? this.item.css as HtmlCSS : this.itemStyle ? this.itemStyle.css as HtmlCSS : {} as HtmlCSS;
        if (this.containerWidth || this.containerHeight) {
            if (css.newsbox !== undefined) {
                return css.newsbox + ` width: ${this.containerWidth}px; height: ${this.containerHeight}px;`;
            } else {
                return ` width: ${this.containerWidth}px; height: ${this.containerHeight}px;`;
            }
        }
        return css.newsbox;
    }

    get allowClicks() {
        return this.propertiesModel.html ? "allowClick" : "";
    }

    // methods
    playVideoIfPresent() {
        this.$nextTick(() => {
            if (!this.allowRender) return;
            const divWrapper = this.$refs.newsWrapper as HTMLDivElement;
            if (divWrapper === undefined) return;
            const objElements = divWrapper.getElementsByTagName("object");
            if (objElements && objElements.length > 0) {
                const obj = objElements[0] as HTMLObjectElement;
                const videoNodes = obj.getElementsByTagName("video");
                if (videoNodes.length > 0) {
                    const video = videoNodes[0];
                    video.play();
                    if (!this.addedVideoEvents) {
                        video.onpause = () => {
                            video.currentTime = 0;
                        };
                        this.addedVideoEvents = true;
                    }
                }
            }
        });
    }

    processHtmlContent(content: string) {
        const parser = new DOMParser();

        const parsedContent = parser.parseFromString(content, "text/html");

        if (!parsedContent) {
            return content;
        }

        const objElements = parsedContent.getElementsByTagName("object");
        if (!objElements || objElements.length === 0) {
            return content;
        }

        const objElement = objElements[0];

        if (this.isIE()) {
            objElement.removeAttribute("classid");
            objElement.removeAttribute("type");
        }
        const embedElements = objElement.getElementsByTagName("embed");
        if (embedElements && embedElements.length > 0) {
            objElement.removeChild(embedElements[0]);
        }

        const paramElements = objElement.getElementsByTagName("param");

        if (!paramElements || paramElements.length === 0) {
            return content;
        }

        const params = [...paramElements];
        const urlParam = params.find(x => x.name.toLocaleLowerCase() === "url");

        const video = document.createElement("video");
        video.height = +objElement.height;
        video.width = +objElement.width;
        video.src = urlParam ? urlParam.value : "";
        video.loop = true;
        objElement.appendChild(video);
        return objElement.outerHTML;
    }

    isIE() {
        return /MSIE|Trident/.test(window.navigator.userAgent);
    }

    setNextItem() {
        this.pauseVideoIfPresent();
        this.$emit("setNextItem", { id: this.item.id } as setNextItem);
    }

    pauseVideoIfPresent() {
        this.$nextTick(() => {
            if (!this.allowRender || this.numberOfPosts === 1) return;
            const divWrapper = this.$refs.newsWrapper as HTMLDivElement;
            if (divWrapper === undefined) return;
            const videoNodes = divWrapper.getElementsByTagName("video");
            if (videoNodes && videoNodes.length > 0) {
                videoNodes[0].pause();
            }
        });
    }
}
